import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { merchantGroupsApi } from 'api';
import {
  CloseDialogResult,
  CopyTextId,
  CrudPage,
  DataGridColumnDefinition,
  Dialog,
  dataGridColumns,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey } from 'enums';
import { useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { MerchantGroup } from 'types';
import { formatUtils } from 'utils';

export const MerchantGroupsPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Merchant, {
    keyPrefix: 'pages.groups',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const queryResult = useQuery(
    QueryKey.MerchantGroups,
    merchantGroupsApi.getAll,
  );

  const [removeState, setRemoveState] = useState<{
    open: boolean;
    data?: MerchantGroup;
  }>({ open: false });

  const { mutate: removeGroup, isLoading: isRemoving } = useMutation(
    merchantGroupsApi.remove,
    {
      onSuccess: () => {
        setRemoveState({ open: false });
        queryClient.invalidateQueries(QueryKey.MerchantGroups);
      },
    },
  );

  const handleCreateOrEdit = useCallback(
    (id: string) =>
      navigate(generatePath(ROUTE_PATH.MERCHANT.GROUP_DETAILS, { id })),
    [navigate],
  );

  const getEditLink = useCallback(
    (group: MerchantGroup) => ({
      to: generatePath(ROUTE_PATH.MERCHANT.GROUP_DETAILS, { id: group.id }),
    }),
    [],
  );

  const handleCloseRemove = useCallback(
    ({ ok, data }: CloseDialogResult<MerchantGroup>) => {
      if (ok && data) {
        removeGroup(data.id);
      } else {
        setRemoveState({ open: false });
      }
    },
    [removeGroup],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<MerchantGroup>[] => [
      {
        header: t('fields.id'),
        valueGetter: (item) => <CopyTextId id={item.id} />,
      },
      { header: t('fields.name'), valueKey: 'name' },
      {
        header: t('fields.shops'),
        multiValueRenderer: {
          itemsGetter: (item) => item.shops || [],
          valueGetter: (shop) => (
            <div key={shop.id}>{formatUtils.formatName(shop)}</div>
          ),
        },
      },
      {
        header: t('fields.operators'),
        multiValueRenderer: {
          itemsGetter: (item) => item.operators || [],
          valueGetter: (operator) => (
            <div key={operator.id}>
              {operator.user?.name || operator.user?.email}
            </div>
          ),
        },
      },
      {
        header: t('fields.operations'),
        valueGetter: (item) => (
          <Fragment>
            {item.payin && <div>{t('fields.payin')}</div>}
            {item.payout && <div>{t('fields.payout')}</div>}
          </Fragment>
        ),
      },
      dataGridColumns.getActionsColumn({
        getEditLink,
        handleRemove: (item: MerchantGroup) =>
          setRemoveState({ open: true, data: item }),
      }),
    ],
    [getEditLink, t],
  );

  return (
    <Fragment>
      <CrudPage
        header={{
          title: t('title'),
          rightContentButton: { onClick: () => handleCreateOrEdit(NEW_ID) },
        }}
        table={{ queryResult, columns }}
      />

      <Dialog
        open={removeState.open}
        title={t('remove_dialog.title')}
        onClose={handleCloseRemove}
        disabled={isRemoving}
        data={removeState.data}
      >
        {removeState.data && <div>{removeState.data.name}</div>}
      </Dialog>
    </Fragment>
  );
};
